/**
 * Smooth closed surfaces from an implicit function (the title logo's puffy letters): "surface nets"
 * over a regular grid. Each grid cell the surface passes through gets one vertex, where the cell's
 * sign-changing edges cross zero, nudged onto the surface; each sign-changing grid edge becomes a quad
 * joining the four cells around it. Every vertex carries the function's gradient as its normal, so
 * the toon bands curve over the letters like cel shading instead of breaking on facets. The output is
 * the same flat, vertex-coloured, unindexed geometry MeshBuilder makes, written straight into arrays
 * (tens of thousands of triangles, built while the title appears, so the loops keep to flat arrays).
 */

import { BufferGeometry, type Color, Float32BufferAttribute, Vector3 } from 'three';

/** Negative inside the shape, positive outside; its zero set is the surface. */
export type Field = (x: number, y: number, z: number) => number;

export interface SurfaceBox {
  min: Vector3;
  max: Vector3;
}

/** The field's gradient at (x, y, z), into `out` (need not be unit length). */
export type Gradient = (x: number, y: number, z: number, out: Vector3) => Vector3;

/** A triangle's colour from its mean normal and its centre (object space). */
export type Paint = (normal: Vector3, at: Vector3) => Color;

/** The field's gradient at (x, y, z) by central differences, normalised: the surface normal. */
export function fieldNormal(f: Field, x: number, y: number, z: number, h: number): Vector3 {
  return new Vector3(
    f(x + h, y, z) - f(x - h, y, z),
    f(x, y + h, z) - f(x, y - h, z),
    f(x, y, z + h) - f(x, y, z - h),
  ).normalize();
}

/** The twelve edges of a cell as pairs of corners; corner c is (c & 1, c >> 1 & 1, c >> 2 & 1). */
const EDGES = [0, 1, 2, 3, 4, 5, 6, 7].flatMap((a) =>
  [1, 2, 4].filter((bit) => (a & bit) === 0).map((bit) => [a, a | bit]));

/**
 * Polygonise the zero set of `f` inside `box` (which must enclose the shape with room to spare) on a
 * grid of `cell`-sized cubes. `grad` is the field's gradient; without it, central differences.
 */
export function surfaceNets(f: Field, box: SurfaceBox, cell: number, paint: Paint, grad?: Gradient): BufferGeometry {
  const gradient: Gradient = grad ?? ((x, y, z, out) => out.copy(fieldNormal(f, x, y, z, cell * 0.25)));
  const count = (a: number): number =>
    Math.max(2, Math.ceil((box.max.getComponent(a) - box.min.getComponent(a)) / cell) + 1);
  const nx = count(0);
  const ny = count(1);
  const nz = count(2);
  const { x: x0, y: y0, z: z0 } = box.min;
  const sy = nx;
  const sz = nx * ny;
  // Samples at every grid point, z fastest: a field may reuse its work along a column (logo.ts does).
  const values = new Float32Array(nx * ny * nz);
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      for (let k = 0; k < nz; k++) values[i + j * sy + k * sz] = f(x0 + i * cell, y0 + j * cell, z0 + k * cell);
    }
  }
  const corner = [0, 1, 2, 3, 4, 5, 6, 7].map((c) => (c & 1) + ((c >> 1) & 1) * sy + ((c >> 2) & 1) * sz);
  // One vertex per cell the surface crosses (cells are indexed by their lowest corner's grid index).
  const vertexOf = new Int32Array(nx * ny * nz).fill(-1);
  const vp: number[] = [];
  const vn: number[] = [];
  const g = new Vector3();
  const cellVertex = (at: number): number => {
    const known = vertexOf[at] ?? -1;
    if (known >= 0) return known;
    let px = 0;
    let py = 0;
    let pz = 0;
    let hits = 0;
    for (const [a = 0, b = 0] of EDGES) {
      const fa = values[at + (corner[a] ?? 0)] ?? 1;
      const fb = values[at + (corner[b] ?? 0)] ?? 1;
      if ((fa < 0) === (fb < 0)) continue;
      const t = fa / (fa - fb);
      px += (a & 1) + ((b & 1) - (a & 1)) * t;
      py += ((a >> 1) & 1) + (((b >> 1) & 1) - ((a >> 1) & 1)) * t;
      pz += ((a >> 2) & 1) + (((b >> 2) & 1) - ((a >> 2) & 1)) * t;
      hits++;
    }
    if (hits === 0) return -1;
    const i = at % nx;
    const j = Math.floor(at / nx) % ny;
    const k = Math.floor(at / sz);
    const p = new Vector3(x0 + (i + px / hits) * cell, y0 + (j + py / hits) * cell, z0 + (k + pz / hits) * cell);
    // One Newton step onto the surface (at most half a cell): smoother silhouettes than the edge
    // average alone.
    gradient(p.x, p.y, p.z, g);
    const len = g.length();
    if (len > 1e-9) {
      const move = Math.max(-cell * 0.5, Math.min(cell * 0.5, f(p.x, p.y, p.z) / len));
      p.addScaledVector(g, -move / len);
    }
    gradient(p.x, p.y, p.z, g).normalize();
    const index = vp.length / 3;
    vp.push(p.x, p.y, p.z);
    vn.push(g.x, g.y, g.z);
    vertexOf[at] = index;
    return index;
  };
  const positions: number[] = [];
  const normals: number[] = [];
  const colours: number[] = [];
  const mean = new Vector3();
  const centre = new Vector3();
  const emit = (a: number, b: number, c: number): void => {
    mean.set(0, 0, 0);
    centre.set(0, 0, 0);
    for (const q of [a, b, c]) {
      mean.x += vn[q * 3] ?? 0;
      mean.y += vn[q * 3 + 1] ?? 0;
      mean.z += vn[q * 3 + 2] ?? 0;
      centre.x += (vp[q * 3] ?? 0) / 3;
      centre.y += (vp[q * 3 + 1] ?? 0) / 3;
      centre.z += (vp[q * 3 + 2] ?? 0) / 3;
    }
    const col = paint(mean.normalize(), centre);
    for (const q of [a, b, c]) {
      positions.push(vp[q * 3] ?? 0, vp[q * 3 + 1] ?? 0, vp[q * 3 + 2] ?? 0);
      normals.push(vn[q * 3] ?? 0, vn[q * 3 + 1] ?? 0, vn[q * 3 + 2] ?? 0);
      colours.push(col.r, col.g, col.b);
    }
  };
  // Each grid edge along axis d with a sign change: a quad over the four cells that share it, wound so
  // it faces from inside to outside (axes u, w follow d cyclically, so u x w = d). Edges on the box's
  // outer faces never change sign: the box encloses the shape.
  const strides = [1, sy, sz];
  for (let k = 1; k < nz - 1; k++) {
    for (let j = 1; j < ny - 1; j++) {
      for (let i = 1; i < nx - 1; i++) {
        const at = i + j * sy + k * sz;
        const fa = values[at] ?? 1;
        for (let d = 0; d < 3; d++) {
          const fb = values[at + (strides[d] ?? 0)] ?? 1;
          if ((fa < 0) === (fb < 0)) continue;
          const su = strides[(d + 1) % 3] ?? 0;
          const sw = strides[(d + 2) % 3] ?? 0;
          const q00 = cellVertex(at - su - sw);
          const q10 = cellVertex(at - sw);
          const q11 = cellVertex(at);
          const q01 = cellVertex(at - su);
          if (q00 < 0 || q10 < 0 || q11 < 0 || q01 < 0) continue;
          if (fa < 0) {
            emit(q00, q10, q11);
            emit(q00, q11, q01);
          } else {
            emit(q00, q11, q10);
            emit(q00, q01, q11);
          }
        }
      }
    }
  }
  const out = new BufferGeometry();
  out.setAttribute('position', new Float32BufferAttribute(positions, 3));
  out.setAttribute('normal', new Float32BufferAttribute(normals, 3));
  out.setAttribute('color', new Float32BufferAttribute(colours, 3));
  out.computeBoundingSphere();
  out.computeBoundingBox();
  return out;
}
